//====== WR data 24/09/2026 — SmartFlow Importer: etapa de Pré-requisitos via BBP (Business
// Blueprint). Lógica PURA da tela — mesmo espírito de itemImporterLogic.js/importerLogic.js: sem
// dependência de UI/API, só formatação/montagem/validação do estado das 3 listas editáveis
// (Depósitos, Grupos de Item, Grupos de PN).
//======
import { formatConfianca } from "components/SmartFlow/itemImporterLogic"
import { normalizarTexto } from "components/SmartFlow/importerLogic"

export { formatConfianca }

// mesmo padrão já usado em SmartFlowParceirosImportados.js/SmartFlowItemsImportados.js
export function formatarData(valor) {
  if (!valor) return "—"
  try {
    return new Date(valor).toLocaleString("pt-BR")
  } catch (error) {
    return "—"
  }
}

let proximoIdLocal = 1
function novoIdLocal() {
  proximoIdLocal += 1
  return `local-${proximoIdLocal}`
}

//====== Converte a sugestão da IA (array de {nome, codigo, confiancaIA, origem, papel?}) em linhas
// editáveis com um `id` local ESTÁVEL — necessário porque a lista é editada em memória (adicionar/
// remover/renomear) antes de salvar; sem um id local a key da grade quebraria a cada edição.
export function sugestaoParaLinhas(itensSugeridos) {
  return (itensSugeridos || []).map((item) => ({
    id: novoIdLocal(),
    nome: item.nome || "",
    codigo: item.codigo || "",
    confiancaIA: item.confiancaIA || null,
    origem: item.origem || "bbp",
    incluir: item.incluir !== false,
    // "ambos" (IA não sabe se o Grupo de PN é de Cliente ou Fornecedor) fica como pendente na tela
    // — ver precisaEscolherPapel — nunca é persistido assim (ver schema do backend).
    ...(item.papel ? { papel: item.papel } : {}),
  }))
}

//====== Linha em branco pro usuário preencher manualmente (seção não encontrada no BBP, ou
// complemento a uma sugestão que já veio da IA) — sempre origem "manual", nunca confiança da IA.
export function novaLinhaManual(extra = {}) {
  return { id: novoIdLocal(), nome: "", codigo: "", confiancaIA: null, origem: "manual", incluir: true, ...extra }
}

//====== Remove campos que só servem pra tela (`id` local, `erroGravacao` da última tentativa de
// gravação no SAP) antes de mandar pro backend — o schema do Mongo não tem esses campos.
export function linhasParaPayload(linhas) {
  return (linhas || [])
    .filter((linha) => String(linha.nome || "").trim())
    .map(({ id, erroGravacao, ...resto }) => ({ ...resto, nome: String(resto.nome).trim() }))
}

//====== WR data 30/09/2026 — Pré-requisitos (BBP): antes de gravar de verdade no SAP, todo Grupo
// de PN incluído (e que ainda não existe no SAP) precisa ter um papel definido — "ambos" (sugestão
// da IA) ou vazio não bastam, porque o SAP não tem um grupo que sirva pros dois ao mesmo tempo
// (Cliente e Fornecedor são tipos separados no cadastro do B1, ver gravarGrupoPnSap).
// @param {Object} linha
// @return {boolean}
export function precisaEscolherPapel(linha) {
  return linha.incluir !== false && linha.origem !== "sap" && !["cliente", "fornecedor"].includes(linha.papel)
}

//====== Uma linha "pendente de gravação" é toda que o usuário marcou pra incluir e que ainda não
// existe de fato no SAP (origem "sap" = já existe, não precisa criar de novo).
// @param {Object} linha
// @return {boolean}
export function precisaGravarNoSap(linha) {
  return linha.incluir !== false && linha.origem !== "sap"
}

//====== Um projeto está com os pré-requisitos "completos" quando o usuário JÁ confirmou a
// validação pelo menos uma vez (`validadoEm` preenchido) — não exige listas não-vazias: um cliente
// pode legitimamente não ter Grupo de Item/PN/Depósito nenhum planejado, e isso é uma decisão do
// usuário, não um erro do sistema. É essa flag que decide se as abas de Item/Parceiro liberam.
export function preRequisitosEstaoCompletos(preRequisitos) {
  return !!preRequisitos?.validadoEm
}

//====== WR data 25/09/2026 — pedido do usuário: além do que o BBP sugere, popular a grade com o
// que JÁ existe cadastrado no SAP (Depósitos/Grupos), com uma tag própria ("sap"), diferente da
// tag do que veio do BBP ("bbp"). Mescla sem duplicar: um item do SAP cujo nome já bate (normalizado
// — sem acento/caixa) com uma linha existente (do BBP ou manual) não entra de novo, só complementa
// o `codigo` se a linha existente ainda não tiver um preenchido. Nunca reordena/remove o que já
// estava na lista — só ACRESCENTA o que falta.
// @param {Array} linhasAtuais já no formato de linha da grade (com id local)
// @param {Array<{code, name}>} itensSap resultado de uma consulta ao vivo na Service Layer
// WR data 01/10/2026 — Motivo da alteração: reportado ao vivo — depois de gravar um Depósito no
// SAP e excluir a linha da grade (ou subir o mesmo BBP de novo), a sugestão da IA reaparecia com
// origem "bbp" (porque já vinha com um `codigo` próprio, igual ao que virou o código real no SAP) e
// a tentativa de "Gravar no SAP" tentava CRIAR DE NOVO, batendo em "esse código já foi cadastrado".
// Antes, esta função só completava o `codigo` de uma linha quando ela ainda não tinha nenhum —
// nunca promovia `origem` pra "sap" nesse caso, então uma sugestão do BBP cujo nome já bate com
// algo que existe de verdade no SAP continuava marcada como "ainda não existe", mesmo existindo.
// Agora o match por nome SEMPRE vence (código/papel REAIS do SAP substituem o que a IA tinha
// sugerido) — se o nome já existe no SAP, a linha é "sap" e não é mais candidata a gravação.
export function mesclarComSap(linhasAtuais, itensSap) {
  const linhas = linhasAtuais || []
  const porNomeNormalizadoSap = new Map((itensSap || []).map((i) => [normalizarTexto(i.name), i]))

  // `type` só vem preenchido pra Grupos de PN (buscarGruposParceiroSap) — bbpgt_CustomerGroup/
  // bbpgt_VendorGroup viram o `papel` já certo, exibido mas travado (o grupo já existe no SAP
  // exatamente com aquele papel, não tem o que escolher).
  const papelPorType = { bbpgt_CustomerGroup: "cliente", bbpgt_VendorGroup: "fornecedor" }

  const linhasComplementadas = linhas.map((linha) => {
    const doSap = porNomeNormalizadoSap.get(normalizarTexto(linha.nome))
    if (!doSap) return linha
    return {
      ...linha,
      codigo: String(doSap.code),
      origem: "sap",
      incluir: true,
      erroGravacao: null,
      ...(papelPorType[doSap.type] ? { papel: papelPorType[doSap.type] } : {}),
    }
  })

  const porNomeNormalizadoLinhas = new Set(linhas.map((l) => normalizarTexto(l.nome)))
  const novasDoSap = (itensSap || [])
    .filter((item) => item.name && !porNomeNormalizadoLinhas.has(normalizarTexto(item.name)))
    .map((item) => ({
      id: novoIdLocal(),
      nome: item.name,
      codigo: String(item.code ?? ""),
      confiancaIA: null,
      origem: "sap",
      incluir: true,
      ...(papelPorType[item.type] ? { papel: papelPorType[item.type] } : {}),
    }))

  return [...linhasComplementadas, ...novasDoSap]
}

//====== Resumo pra exibir no aviso de bloqueio das abas de Item/Parceiro (ex.: "3 depósitos, 2
// grupos de item, nenhum grupo de PN — confirmados em 24/09/2026").
export function resumoPreRequisitos(preRequisitos) {
  if (!preRequisitos) return null
  const depositos = (preRequisitos.depositos || []).length
  const gruposItem = (preRequisitos.gruposItem || []).length
  const gruposPn = (preRequisitos.gruposPn || []).length
  return { depositos, gruposItem, gruposPn }
}
